import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import type { Product } from "@/types/product";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useCompare } from "@/context/CompareContext";

interface Props {
  product: Product | null;
  onClose: () => void;
}

function StarRow({ rating, reviews }: { rating: number; reviews: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-px">
        {[1, 2, 3, 4, 5].map((s) => (
          <i
            key={s}
            className={`text-sm ${
              s <= Math.floor(rating)
                ? "ri-star-fill text-amber-400"
                : s - rating < 1
                ? "ri-star-half-fill text-amber-400"
                : "ri-star-line text-gray-300"
            }`}
          />
        ))}
      </div>
      <span className="text-sm font-bold text-amber-600 tabular-nums">{rating.toFixed(1)}</span>
      <span className="text-xs text-gray-400">
        ({reviews >= 1000 ? `${(reviews / 1000).toFixed(1)}k` : reviews} reviews)
      </span>
    </div>
  );
}

export default function QuickViewModal({ product, onClose }: Props) {
  const [added, setAdded] = useState(false);
  const [heartPop, setHeartPop] = useState(false);
  const [qty, setQty] = useState(1);
  const [imgZoomed, setImgZoomed] = useState(false);

  const { addItem } = useCart();
  const { isWishlisted, toggleWishlist, openWishlist } = useWishlist();
  const { isInCompare, toggleCompare, canAdd } = useCompare();

  const wishlisted = product ? isWishlisted(product.id) : false;
  const inCompare = product ? isInCompare(product.id) : false;
  const compareDisabled = !inCompare && !canAdd;

  // Reset state when product changes
  useEffect(() => {
    setAdded(false);
    setQty(1);
    setImgZoomed(false);
  }, [product?.id]);

  // Lock body scroll
  useEffect(() => {
    if (!product) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [product]);

  // Escape key
  const handleKey = useCallback(
    (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); },
    [onClose],
  );
  useEffect(() => {
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  if (!product) return null;

  const saveDollars = product.originalPrice ? product.originalPrice - product.price : null;
  const savePct = saveDollars
    ? Math.round((saveDollars / product.originalPrice!) * 100)
    : null;

  const handleAddToCart = () => {
    if (!product.inStock) return;
    addItem(
      {
        id: product.id,
        variantId: product.variantId,
        name: product.name,
        brand: product.brand,
        price: product.price,
        image: product.image,
      },
      qty,
    );
    setAdded(true);
    setTimeout(() => setAdded(false), 2400);
  };

  const handleWishlist = () => {
    toggleWishlist({
      id: product.id,
      variantId: product.variantId,
      name: product.name,
      brand: product.brand,
      price: product.price,
      originalPrice: product.originalPrice,
      image: product.image,
      inStock: product.inStock,
      category: product.category,
    });
    if (!wishlisted) {
      setHeartPop(true);
      setTimeout(() => setHeartPop(false), 500);
      openWishlist();
    }
  };

  const modal = (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Panel */}
      <div
        className="relative z-10 bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col md:flex-row shadow-2xl animate-[quickViewIn_0.22s_ease-out]"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "quickViewIn 0.22s ease-out" }}
      >
        {/* ── Left: Image ── */}
        <div className="relative md:w-2/5 w-full h-64 md:h-auto bg-gray-50 shrink-0 flex items-center justify-center overflow-hidden">
          {/* Sale badge */}
          {saveDollars && product.inStock && (
            <span className="absolute top-3 left-3 z-10 bg-red-500 text-white text-[10px] font-extrabold px-2.5 py-1 rounded">
              Save ${saveDollars.toLocaleString()}
            </span>
          )}
          {/* Sold out overlay */}
          {!product.inStock && (
            <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10">
              <span className="bg-gray-700 text-white text-xs font-bold px-3 py-1.5 rounded-md">Sold Out</span>
            </div>
          )}
          {/* New badge */}
          {product.isNew && product.inStock && (
            <span className="absolute top-3 right-3 z-10 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
              New
            </span>
          )}
          <img
            src={product.image}
            alt={product.name}
            className={`w-full h-full object-contain p-6 transition-transform duration-500 cursor-zoom-in select-none ${
              imgZoomed ? "scale-125 cursor-zoom-out" : "scale-100"
            }`}
            onClick={() => setImgZoomed((v) => !v)}
          />
          {/* Zoom hint */}
          {!imgZoomed && (
            <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-black/30 text-white text-[9px] font-semibold px-2 py-1 rounded-full pointer-events-none">
              <i className="ri-zoom-in-line text-[10px]"></i>
              Click to zoom
            </div>
          )}
        </div>

        {/* ── Right: Details ── */}
        <div className="flex flex-col flex-1 overflow-y-auto">
          {/* Close btn */}
          <button
            onClick={onClose}
            className="absolute top-3.5 right-3.5 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors cursor-pointer z-20"
          >
            <i className="ri-close-line text-lg"></i>
          </button>

          <div className="p-6 flex flex-col gap-4">
            {/* Brand + name */}
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-1">{product.brand}</p>
              <h2 className="text-lg font-extrabold text-gray-900 leading-snug pr-8">{product.name}</h2>
            </div>

            {/* Stars */}
            <StarRow rating={product.rating} reviews={product.reviews} />

            {/* Price */}
            <div>
              {product.originalPrice ? (
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-2xl font-extrabold text-red-600">${product.price.toLocaleString()}</span>
                  <span className="text-sm text-gray-400 line-through">${product.originalPrice.toLocaleString()}</span>
                  {savePct && (
                    <span className="text-xs font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                      {savePct}% off
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-2xl font-extrabold text-gray-900">${product.price.toLocaleString()}</span>
              )}
              <div className="flex items-center gap-3 mt-1.5">
                <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                  <i className="ri-truck-line"></i> Free Shipping
                </span>
                <span className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                  <i className="ri-shield-check-line"></i> Mfr. Warranty
                </span>
              </div>
            </div>

            {/* Key specs row */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
                <i className="ri-battery-charge-line text-sm"></i>
                {product.capacity}
              </span>
              <span className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
                <i className="ri-flashlight-line text-sm"></i>
                {product.watts}
              </span>
              {product.isLimitedStock && product.inStock && (
                <span className="flex items-center gap-1.5 bg-orange-50 border border-orange-200 text-orange-600 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
                  <i className="ri-alarm-warning-line text-sm"></i>
                  Limited Stock
                </span>
              )}
            </div>

            {/* Highlights */}
            {product.highlights.length > 0 && (
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Highlights</p>
                <ul className="space-y-1.5">
                  {product.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-gray-700 leading-snug">
                      <span className="mt-0.5 w-4 h-4 flex items-center justify-center shrink-0">
                        <i className="ri-check-line text-emerald-500 text-sm font-bold"></i>
                      </span>
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Qty + Add to Cart */}
            <div className="flex items-center gap-2">
              {/* Qty stepper */}
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-8 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-subtract-line text-sm"></i>
                </button>
                <span className="w-9 h-9 flex items-center justify-center text-sm font-bold text-gray-900 tabular-nums select-none border-x border-gray-200">
                  {qty}
                </span>
                <button
                  onClick={() => setQty((q) => Math.min(99, q + 1))}
                  className="w-8 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-add-line text-sm"></i>
                </button>
              </div>

              {/* Add to cart */}
              <button
                onClick={handleAddToCart}
                disabled={!product.inStock}
                className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all whitespace-nowrap cursor-pointer ${
                  added
                    ? "bg-emerald-500 text-white"
                    : product.inStock
                    ? "bg-amber-500 hover:bg-amber-400 active:scale-95 text-white"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
              >
                {added ? (
                  <span className="flex items-center justify-center gap-1.5">
                    <i className="ri-check-line"></i> Added to Cart
                  </span>
                ) : product.inStock ? (
                  "Add to Cart"
                ) : (
                  "Out of Stock"
                )}
              </button>

              {/* Wishlist */}
              <button
                onClick={handleWishlist}
                title={wishlisted ? "Remove from wishlist" : "Save to wishlist"}
                className={`w-10 h-10 flex items-center justify-center rounded-lg border transition-all cursor-pointer ${
                  heartPop ? "scale-125" : "scale-100"
                } ${
                  wishlisted
                    ? "bg-rose-500 border-rose-500 text-white"
                    : "bg-white border-gray-200 text-gray-400 hover:text-rose-500 hover:border-rose-300"
                }`}
                style={{ transition: "transform 0.18s ease, background-color 0.15s" }}
              >
                <i className={wishlisted ? "ri-heart-fill" : "ri-heart-line"}></i>
              </button>
            </div>

            {/* Compare + View Full Details */}
            <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
              <button
                onClick={() => toggleCompare(product)}
                disabled={compareDisabled}
                className={`flex items-center gap-1.5 py-2 px-3 rounded-lg text-xs font-semibold border transition-all whitespace-nowrap cursor-pointer ${
                  inCompare
                    ? "bg-amber-50 border-amber-300 text-amber-700"
                    : compareDisabled
                    ? "bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed"
                    : "bg-white border-gray-200 text-gray-500 hover:border-amber-300 hover:text-amber-600"
                }`}
              >
                <i className={inCompare ? "ri-checkbox-fill text-amber-500" : "ri-checkbox-blank-line"}></i>
                {inCompare ? "In Compare" : "Compare"}
              </button>

              <Link
                to={`/shop/${product.id}`}
                onClick={onClose}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-semibold border border-gray-200 text-gray-600 hover:border-amber-300 hover:text-amber-600 hover:bg-amber-50 transition-all whitespace-nowrap cursor-pointer"
              >
                View Full Details
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
